import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useYjsCollaboration } from "pages/modules/kb/helpers/YjsCollaborationContext";
import TipTapEditor from "components/editor/TipTapEditor";
import * as Y from "yjs";
import { Box, Stack, useTheme } from "@mui/material";
import { CircularLoader } from "components";
import { fromUint8Array, toUint8Array } from "js-base64";
import {
  generateUserColor,
  setFirstH1AsFilename,
  tiptapJsonToYjsUpdate,
} from "../helpers";
import { debugConsole } from "utilz/helpers/debugConsole";
import { KbFileDetailResponse } from "shared";

interface KbCollaborativeEditorProps {
  kbRecordId: string;
  snapshotId: string;
  initialContent?: any;
  isEditable?: boolean;
  userId: string;
  userName: string;
  mspId: string;
  onContentChange?: (content: { content: any; textContent: string }) => void;
  autosave?: boolean;
  recordVisibility: "unsaved" | "draft" | "public" | "private";
  onSave?: ({
    content,
    htmlContent,
    textContent,
  }: {
    content: Record<string, unknown>;
    htmlContent: string;
    textContent: string;
  }) => void;
}

/**
 * Wrapper component for TipTap editor with collaboration enabled
 * Uses existing TipTapEditor with collaboration props
 */
export function KbCollaborativeEditor({
  kbRecordId,
  snapshotId,
  initialContent,
  isEditable = true,
  userId,
  userName,
  mspId,
  onContentChange,
  autosave = true,
  onSave,
  recordVisibility,
}: KbCollaborativeEditorProps) {
  const theme = useTheme();

  // Use the ydoc and awareness from context instead of creating new ones
  const collaborationContext = useYjsCollaboration();

  if (!collaborationContext) {
    throw new Error(
      "KbCollaborativeEditor must be used within YjsCollaborationProvider",
    );
  }

  const { ydoc, awareness, provider } = collaborationContext;
  const [isJoinedSession, setIsJoinedSession] = useState(false);
  const lastPublishedVersionRef = useRef<number>(0);
  const currentContentRef = useRef<any>(null);
  const contentVersionRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Listen for join success from the context provider
  useEffect(() => {
    if (!provider) return;

    /**
     * Fallback: Convert TipTap JSON content to Yjs update and apply to ydoc
     * Used when server returns empty/missing yjs_update
     */
    const applyContentFallback = () => {
      // First check if ydoc already has content - don't apply fallback if it does
      const existingFragment = ydoc.getXmlFragment("default");
      if (existingFragment.toArray().length > 0) {
        debugConsole(
          "[Content Fallback] Ydoc already has content, skipping fallback",
        );
        return;
      }

      if (
        initialContent &&
        typeof initialContent === "object" &&
        Object.keys(initialContent).length > 0
      ) {
        try {
          debugConsole(
            "[Content Fallback] Converting TipTap JSON to Yjs update...",
            initialContent,
          );
          const yjsUpdate = tiptapJsonToYjsUpdate(initialContent, "default");
          Y.applyUpdate(ydoc, yjsUpdate, "local-init");

          // Emit socket event to save generated Yjs to backend for other collaborators
          if (provider) {
            provider.publishGeneratedYjs(yjsUpdate);
          }

          // Debug: verify content was applied
          const fragment = ydoc.getXmlFragment("default");
          debugConsole(
            "[Content Fallback] Successfully applied. Fragment children:",
            fragment.toArray().length,
          );
        } catch (fallbackError) {
          debugConsole(
            "[Content Fallback] Failed to convert content:",
            fallbackError,
          );
        }
      } else {
        debugConsole(
          "[Content Fallback] No valid initialContent available for fallback",
        );
      }
    };

    /**
     * Check if a Yjs update is effectively empty (no actual content)
     * A minimal Yjs update with no content is typically very small (just struct/delete counters)
     */
    const isUpdateEmpty = (update: Uint8Array): boolean => {
      // An update with just the header and no structs is ~4-6 bytes
      // Real content usually exceeds 10 bytes
      return update.length <= 10;
    };

    const handleJoinSuccess = (data: any) => {
      debugConsole("Join session successful:", data);
      debugConsole("[DEBUG] Response keys:", data ? Object.keys(data) : "null");
      debugConsole("[DEBUG] documentState:", data?.documentState);
      debugConsole("[DEBUG] update field:", data?.update);

      try {
        // Extract base64 update from response - check both possible locations
        const base64Update = data?.documentState?.update || data?.update;

        if (base64Update) {
          // Decode base64 to Uint8Array
          const decodedUpdate = toUint8Array(base64Update);

          debugConsole("[DEBUG] Update size:", decodedUpdate.length, "bytes");

          // Check if update is effectively empty
          if (isUpdateEmpty(decodedUpdate)) {
            debugConsole(
              "[handleJoinSuccess] Received empty/minimal update from server, using content fallback",
            );
            applyContentFallback();
          } else {
            // Apply update to ydoc with 'remote' origin to prevent echo
            Y.applyUpdate(ydoc, decodedUpdate, "remote");

            // Debug: Check what's in the Yjs doc after applying update
            const fragment = ydoc.getXmlFragment("default");
            debugConsole(
              "[DEBUG] After applyUpdate - fragment children:",
              fragment.toArray().length,
            );

            // If fragment is still empty after applying, use fallback
            if (fragment.toArray().length === 0) {
              debugConsole(
                "[handleJoinSuccess] Update applied but fragment empty, using content fallback",
              );
              applyContentFallback();
            }
          }
        } else {
          debugConsole(
            "[handleJoinSuccess] No documentState.update in response, using content fallback",
          );
          applyContentFallback();
        }
      } catch (error) {
        debugConsole(
          "[handleJoinSuccess] Failed to apply initial state:",
          error,
        );
        // Try fallback on error
        applyContentFallback();
      }

      // Mark session as joined after sync
      setIsJoinedSession(true);
    };

    provider.on("joinSuccess", handleJoinSuccess);

    return () => {
      provider.off("joinSuccess", handleJoinSuccess);
    };
  }, [provider, initialContent, ydoc]);

  const collaborationUser = useMemo(
    () => ({
      name: userName || "Anonymous",
      color: generateUserColor(userId || "", theme),
    }),
    [userName, userId, theme],
  );

  // Wrap awareness in a provider-like object for CollaborationCaret
  const providerWithAwareness = useMemo(
    () => ({
      awareness: awareness,
    }),
    [awareness],
  );

  // Use refs to persist across renders and avoid memory leaks
  const updateQueueRef = useRef<Uint8Array[]>([]);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateHandler = useCallback(
    (update: Uint8Array, origin: unknown) => {
      // CRITICAL: Don't publish updates until session is joined
      if (!isJoinedSession) {
        return;
      }

      // Skip updates that come from remote (marked with "remote" origin)
      if (origin === "remote") {
        return;
      }

      if (!provider) {
        return;
      }

      // Add update to queue
      updateQueueRef.current.push(update);

      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Debounce: send after 100ms of inactivity to batch more keystrokes
      debounceTimeoutRef.current = setTimeout(() => {
        if (updateQueueRef.current.length === 0 || !provider) return;
        // Merge all queued updates
        const mergedUpdate = Y.mergeUpdates(updateQueueRef.current);

        provider.publishUpdate(mergedUpdate);
        // Clear queue
        updateQueueRef.current = [];
      }, 100);
    },
    [isJoinedSession, provider],
  );

  useEffect(() => {
    ydoc.on("update", updateHandler);

    return () => {
      ydoc.off("update", updateHandler);
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [ydoc, updateHandler]);

  // Track content changes from TipTap editor
  const handleContentChange = (content: {
    content: any;
    htmlContent: string;
    textContent: string;
  }) => {
    // Store current content in ref for interval publishing
    currentContentRef.current = content;
    // Increment version counter for cheap change detection
    contentVersionRef.current += 1;

    // Call parent's onContentChange if provided
    if (onContentChange) {
      onContentChange(content);
    }
  };

  // Interval-based publishing every 1 minute
  useEffect(() => {
    // Only start interval if editor is editable, session joined, and provider is ready
    if (
      !isEditable ||
      !isJoinedSession ||
      !provider ||
      recordVisibility === "unsaved"
    ) {
      return;
    }

    debugConsole(
      "[Interval Publishing] Starting 1-minute interval for TipTap updates",
    );

    intervalRef.current = setInterval(async () => {
      try {
        const currentContent = currentContentRef.current;

        // Skip if no content available yet
        if (!currentContent) {
          debugConsole(
            "[Interval Publishing] No content available yet, skipping",
          );
          return;
        }

        // Only publish if content version has changed since last publish
        // Using version counter instead of expensive JSON.stringify comparison
        if (contentVersionRef.current !== lastPublishedVersionRef.current) {
          debugConsole(
            "[Interval Publishing] Content changed, publishing update",
          );
          const packet = {
            content: currentContent.content,
            filename: await setFirstH1AsFilename({
              content: currentContent,
              currentValue: "",
            }),
          };

          provider.publishTipTapUpdate(packet);
          lastPublishedVersionRef.current = contentVersionRef.current;
        } else {
          debugConsole(
            "[Interval Publishing] No content changes detected, skipping publish",
          );
        }
      } catch (error) {
        debugConsole(
          "[Interval Publishing] Error publishing TipTap update:",
          error,
        );
      }
    }, 5000); // 5 seconds

    return () => {
      if (intervalRef.current) {
        debugConsole("[Interval Publishing] Clearing interval");
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isEditable, isJoinedSession, provider]);

  // If isEditable is false, show TipTap immediately
  // If isEditable is true, only show TipTap when joinSession is successful

  // Only render editor after session is joined (to ensure data is loaded first)

  if (isEditable && !isJoinedSession) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "calc(100vh - 64px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularLoader />
      </Box>
    );
  }
  return (
    <>
      <TipTapEditor
        enableCollaboration={isEditable ? true : false}
        yjsDocument={ydoc}
        awarenessProvider={providerWithAwareness as any}
        collaborationUser={collaborationUser}
        // In collaboration mode, content comes from Yjs document, not from prop
        // Only pass content when NOT in collaboration mode (view mode)
        content={isEditable ? undefined : initialContent}
        isEditable={isEditable}
        forceFirstNodeH1={true}
        autosave={autosave}
        onContentChange={handleContentChange}
        onSave={onSave}
      />
    </>
  );
}
